import React, { useEffect, useRef, useState } from "react";
import {
  FaBalanceScale,
  FaBolt,
  FaBroadcastTower,
  FaCalendarCheck,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaCogs,
  FaDesktop,
  FaFileExcel,
  FaFileImport,
  FaLayerGroup,
  FaMedal,
  FaPlayCircle,
  FaQrcode,
  FaRandom,
  FaSitemap,
  FaStopwatch,
  FaThLarge,
  FaUserEdit,
  FaUsers,
  FaWeight,
} from "react-icons/fa";

const APP_LOGO = "/logo_monapplitournoi_512.png";

const CompetitionStepCard = ({ step, index }) => {
  const Icon = step.icon;

  return (
    <article className="group relative rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/10 dark:border-white/10 dark:bg-slate-950/40 dark:hover:border-orange-400/40">
      <div className="flex gap-4">
        <div className="relative shrink-0">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500 text-sm font-bold text-white shadow-lg shadow-orange-500/20">
            {index + 1}
          </div>

          <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-white bg-blue-600 text-white shadow-sm dark:border-slate-950">
            <Icon className="h-3 w-3" />
          </div>
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-bold leading-snug text-slate-950 dark:text-white sm:text-base">
            {step.title}
          </h3>

          <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {step.desc}
          </p>
        </div>
      </div>
    </article>
  );
};

const AboutLogoShowcase = () => {
  return (
    <div className="relative mx-auto flex w-full max-w-[460px] items-center justify-center">
      <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-blue-500/15 via-orange-400/15 to-transparent blur-3xl" />

      <div className="relative flex aspect-square w-full max-w-[390px] items-center justify-center rounded-[3rem] border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-slate-950/70">
        <div className="absolute inset-4 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-white dark:from-white/5 dark:to-white/[0.02]" />

        <img
          src={APP_LOGO}
          alt="Logo MonAppliTournoi"
          className="relative z-10 h-full max-h-[300px] w-full max-w-[300px] object-contain drop-shadow-xl"
          loading="lazy"
        />

        <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-orange-400/20 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-blue-500/20 blur-2xl" />
      </div>
    </div>
  );
};

const useInView = (options = { threshold: 0.15 }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, options);

    observer.observe(element);

    return () => observer.disconnect();
  }, [options]);

  return [ref, inView];
};

const features = [
  {
    icon: FaCalendarCheck,
    title: "Création rapide",
    desc: "Préparez vos compétitions, catégories, tapis et paramètres en quelques clics.",
  },
  {
    icon: FaFileImport,
    title: "Import compétiteurs",
    desc: "Importez vos listings et gagnez du temps lors de la préparation du tournoi.",
  },
  {
    icon: FaBalanceScale,
    title: "Pesée simplifiée",
    desc: "Contrôlez les présences, les poids et les catégories avec une interface claire.",
  },
  {
    icon: FaSitemap,
    title: "Poules & tableaux",
    desc: "Générez vos répartitions, poules, tableaux, repêchages et classements.",
  },
];

const competitionSteps = [
  {
    icon: FaCogs,
    title: "Création de la compétition",
    desc: "Paramétrez la compétition : nom, date, lieu, disciplines, catégories, tapis, modes de répartition et options d’organisation.",
  },
  {
    icon: FaFileExcel,
    title: "Importation des combattants",
    desc: "Ajoutez les compétiteurs par fichier Excel, saisie manuelle ou formulaire d’inscription en ligne envoyé aux clubs invités.",
  },
  {
    icon: FaWeight,
    title: "Pesées & présences",
    desc: "Le jour J, contrôlez les présences, les poids, les catégories et les éventuels ajustements avant la répartition.",
  },
  {
    icon: FaRandom,
    title: "Répartition des présents",
    desc: "Répartissez les compétiteurs en poules de 2, 3, 4, 5 ou 6, ou en tableaux base 8, 16, 32 ou 64.",
  },
  {
    icon: FaThLarge,
    title: "Poules, tableaux & repêchages",
    desc: "Choisissez le mode adapté : élimination directe, repêchage systématique ou organisation spécifique selon le format de compétition.",
  },
  {
    icon: FaPlayCircle,
    title: "Affectation sur les tapis",
    desc: "Affectez les distributions aux tapis, organisez l’ordre de passage et lancez les combats sur les surfaces disponibles.",
  },
  {
    icon: FaDesktop,
    title: "Tables d’arbitrage & scoreboards",
    desc: "Les tables suivent les combats, saisissent les résultats et peuvent afficher le tableau de score public en temps réel.",
  },
  {
    icon: FaBroadcastTower,
    title: "Résultats publics en direct",
    desc: "Les résultats, classements, podiums et statistiques sont accessibles au public et mis à jour au fil de la compétition.",
  },
];

const AboutFeatureCard = ({ icon: Icon, title, desc }) => {
  return (
    <article className="group rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/10 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-orange-400/40">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 ring-1 ring-orange-100 transition group-hover:bg-orange-500 group-hover:text-white dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-300/20">
        <Icon className="h-5 w-5" />
      </div>

      <h3 className="text-base font-bold text-slate-950 dark:text-white">
        {title}
      </h3>

      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {desc}
      </p>
    </article>
  );
};

const AboutHighlightCard = ({ icon: Icon, title, desc }) => {
  return (
    <article className="rounded-3xl border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/[0.04]">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-300/20">
        <Icon className="h-5 w-5" />
      </div>

      <h3 className="mt-4 text-base font-bold text-slate-950 dark:text-white">
        {title}
      </h3>

      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {desc}
      </p>
    </article>
  );
};

const About = () => {
  const [topRef, topInView] = useInView();
  const [bottomRef, bottomInView] = useInView();

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

      <div
        ref={topRef}
        className={`relative grid items-center gap-8 rounded-[2rem] border border-slate-200 bg-white/85 p-4 shadow-xl shadow-slate-950/5 backdrop-blur transition-all duration-700 dark:border-white/10 dark:bg-white/[0.04] sm:p-6 lg:grid-cols-[0.9fr_1.1fr] lg:p-8 ${
          topInView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        <AboutLogoShowcase />

        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaBolt className="h-3.5 w-3.5" />
            Organisation rapide
          </div>

          <h3 className="mt-5 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl lg:text-4xl">
            Préparez votre compétition avec une interface claire, moderne et
            pensée pour le jour J.
          </h3>

          <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-slate-300">
            MonAppliTournoi accompagne les clubs, comités et organisateurs dans
            toutes les étapes d’un tournoi : préparation des compétiteurs,
            création des catégories, répartition en poules ou tableaux, suivi
            des combats, résultats publics et édition des podiums.
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {features.map((feature) => (
              <AboutFeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </div>

      <div
        ref={bottomRef}
        className={`relative mt-8 rounded-[2rem] border border-slate-200 bg-slate-50/90 p-4 shadow-xl shadow-slate-950/5 backdrop-blur transition-all duration-700 dark:border-white/10 dark:bg-white/[0.03] sm:p-6 lg:p-8 ${
          bottomInView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700 dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-blue-300">
              <FaClipboardList className="h-3.5 w-3.5" />
              Déroulé de la compétition
            </div>

            <h3 className="mt-5 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl lg:text-4xl">
              Un parcours clair, de la préparation du tournoi jusqu’aux
              résultats publics.
            </h3>

            <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-slate-300">
              MonAppliTournoi accompagne chaque étape de l’organisation :
              création, inscriptions, pesées, répartition, affectation sur les
              tapis, suivi des combats et publication des résultats en temps
              réel.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:min-w-[420px]">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-white/10 dark:bg-slate-950/40">
              <FaUsers className="mx-auto h-5 w-5 text-blue-600 dark:text-blue-300" />
              <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                Clubs
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                invités
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-white/10 dark:bg-slate-950/40">
              <FaLayerGroup className="mx-auto h-5 w-5 text-orange-600 dark:text-orange-300" />
              <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                Poules
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                2 à 6
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-white/10 dark:bg-slate-950/40">
              <FaSitemap className="mx-auto h-5 w-5 text-emerald-600 dark:text-emerald-300" />
              <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                Tableaux
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                8 à 64
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-white/10 dark:bg-slate-950/40">
              <FaBroadcastTower className="mx-auto h-5 w-5 text-red-600 dark:text-red-300" />
              <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                Live
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                public
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {competitionSteps.map((step, index) => (
            <CompetitionStepCard key={step.title} step={step} index={index} />
          ))}
        </div>

        <div className="mt-8 rounded-[2rem] border border-orange-200 bg-orange-50 p-5 dark:border-orange-400/20 dark:bg-orange-500/10 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <h4 className="text-xl font-bold text-slate-950 dark:text-white">
                Une organisation plus fluide pour tous les acteurs du tournoi.
              </h4>

              <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                L’organisateur garde une vision globale, les tables d’arbitrage
                suivent leurs combats, les coachs consultent l’avancement, et le
                public accède aux résultats sans application à installer.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href="#tarifs"
                className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:bg-orange-600"
              >
                Découvrir les formules
              </a>

              <a
                href="#contact"
                className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:text-orange-600 dark:border-white/10 dark:bg-slate-950/40 dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
              >
                Demander une démonstration
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
